import {
    LEAK_ENTITY_STATUSES,
    POI_ENTITY_STATUSES,
    type EntityStatusDefinition,
    type LeakEntityStatus,
    type LeakStatusDefinition,
    type PoiEntityStatus,
} from './entityStatus';
import { POI_DISC_PATH, POI_GLYPH_PATH, POI_PIN_PATH } from './poiMarkerArt';

// Marker artwork for the split POI / Find & Fix leak entity model, generated from the
// designer's SVG pack. The pack's language: one shared pin silhouette, a pale status disc,
// and a saturated centre glyph — POI statuses use symbol strokes, leak statuses use the
// droplet, and the taller badged base adds a satellite circle repeating the glyph (the
// design's "a follow-up event exists" cue). All colours and glyphs resolve through the
// entityStatus registries, so a design change is a table edit here and nowhere else.
//
// The pack's Figma exports use <filter> shadows, which do not rasterise reliably through
// the canvas path MapLibre images take; as with the pre-split pins, they are re-expressed
// with rasterisation-safe primitives (radial-gradient ground ellipse + an offset 10%-black
// pin copy). The pack's ground-shadow geometry (rx 7, ry 2.5) supersedes the old rx 9.
//
// Anchor convention (load-bearing for the coincident-stack fan): the pin tip sits 6px
// above the canvas bottom in BOTH bases — 52×60 standard (tip y=54) and 52×80 badged
// (tip y=74, pin translated +20 for the badge) — so one `icon-anchor: 'bottom'` +
// `icon-offset [0, 6]` convention serves every marker.

export const MARKER_WIDTH = 52;
export const STANDARD_MARKER_HEIGHT = 60;
export const BADGED_MARKER_HEIGHT = 80;
export const SELECTED_MARKER_WIDTH = 104;
export const SELECTED_MARKER_HEIGHT = 144;

// ─── Glyphs (from the design pack; stroke-based symbols for POIs, droplet for leaks) ────

/** White inner circle — Awaiting Assessment. */
const GLYPH_CIRCLE = (color: string): string =>
    `<path d="M33 25C33 21.134 29.866 18 26 18C22.134 18 19 21.134 19 25C19 28.866 22.134 32 26 32C29.866 32 33 28.866 33 25Z" fill="${color}"/>`;

const GLYPH_CHECK = (color: string): string =>
    `<path d="M20 25.5L24.3333 30L33 21" stroke="${color}" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>`;

const GLYPH_CROSS = (color: string): string =>
    `<path d="M32 19L20 31M20 19L32 31" stroke="${color}" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>`;

const GLYPH_EXCLAMATION = (color: string): string =>
    `<path d="M26 18V26.3333M26 31.6667H26.0108" stroke="${color}" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>`;

const GLYPH_QUESTION = (color: string): string =>
    `<path d="M21 20.4061C21.4033 19.2704 22.1992 18.3128 23.2469 17.7028C24.2946 17.0928 25.5264 16.8699 26.7241 17.0734C27.9219 17.2769 29.0083 17.8938 29.7909 18.8148C30.5735 19.7358 31.0018 20.9015 31 22.1054C31 25.5039 25.8542 27.2031 25.8542 27.2031M25.9917 34H26.0088" stroke="${color}" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>`;

/** Interim Unclassified symbol: a dash — "no valid value". Pending the UX treatment. */
const GLYPH_DASH = (color: string): string =>
    `<path d="M20 25H32" stroke="${color}" stroke-width="4" stroke-linecap="round"/>`;

/** The leak family's droplet (the pre-split pin glyph, verbatim in the pack). */
const GLYPH_DROPLET = (color: string): string => `<path d="${POI_GLYPH_PATH}" fill="${color}"/>`;

const POI_GLYPHS: Record<PoiEntityStatus, (color: string) => string> = {
    awaiting_assessment: GLYPH_CIRCLE,
    leak_found: GLYPH_CHECK,
    no_leak_found: GLYPH_CROSS,
    inconclusive: GLYPH_QUESTION,
    unable_to_check: GLYPH_EXCLAMATION,
    unclassified: GLYPH_DASH,
};

// Badge glyphs are the same symbols scaled into the badge circle's coordinate space
// (centre 25.5, 18.5 — from the pack's badged exports).
const BADGE_CHECK = (color: string): string =>
    `<path d="M21 19L24 22L30 16" stroke="${color}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>`;
const BADGE_CROSS = (color: string): string =>
    `<path d="M29 15L22 22M22 15L29 22" stroke="${color}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>`;
const BADGE_EXCLAMATION = (color: string): string =>
    `<path d="M25.5 12.5V19.8333M25.5 25.1667H25.5108" stroke="${color}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>`;
const BADGE_QUESTION = (color: string): string =>
    `<path d="M22.0059 15.2376C22.2881 14.4359 22.8453 13.7599 23.5787 13.3294C24.3121 12.8988 25.1743 12.7414 26.0128 12.8851C26.8512 13.0287 27.6116 13.4642 28.1595 14.1143C28.7073 14.7644 29.0071 15.5872 29.0059 16.437C29.0059 18.836 25.4038 20.0355 25.4038 20.0355M25.5 24.8333H25.512" stroke="${color}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>`;
const BADGE_DASH = (color: string): string =>
    `<path d="M21 18L30 18" stroke="${color}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>`;

/** Badge glyph per badged leak status (the badge repeats the status's symbol). */
const LEAK_BADGE_GLYPHS: Partial<Record<LeakEntityStatus, (color: string) => string>> = {
    inconclusive: BADGE_QUESTION,
    unable_to_check: BADGE_EXCLAMATION,
    rechecked: BADGE_CHECK,
    initial_repair_failed: BADGE_CROSS,
    recheck_failed: BADGE_CROSS,
    unclassified: BADGE_DASH,
};

// ─── Bases (rasterisation-safe shadow primitives, pack geometry) ───────────────────────

const GROUND_SHADOW = (cy: number): string => `
  <defs>
    <radialGradient id="entityGroundShadow" cx="50%" cy="50%" r="50%">
      <stop offset="55%" stop-color="black" stop-opacity="0.2"/>
      <stop offset="100%" stop-color="black" stop-opacity="0"/>
    </radialGradient>
  </defs>
  <ellipse cx="26" cy="${String(cy)}" rx="7" ry="2.5" fill="url(#entityGroundShadow)"/>`;

const PIN_BODY = (discColor: string, glyphMarkup: string): string => `
    <path fill-rule="evenodd" clip-rule="evenodd" d="${POI_PIN_PATH}" fill="black" fill-opacity="0.1" transform="translate(0 2)"/>
    <path fill-rule="evenodd" clip-rule="evenodd" d="${POI_PIN_PATH}" fill="white"/>
    <path d="${POI_DISC_PATH}" fill="${discColor}"/>
    ${glyphMarkup}`;

/** Standard 52×60 marker: pin + disc + glyph, tip at (26, 54). */
export function createStandardMarkerSvg(discColor: string, glyphMarkup: string): string {
    return `<svg width="${String(MARKER_WIDTH)}" height="${String(STANDARD_MARKER_HEIGHT)}" viewBox="0 0 ${String(MARKER_WIDTH)} ${String(STANDARD_MARKER_HEIGHT)}" fill="none" xmlns="http://www.w3.org/2000/svg">
  ${GROUND_SHADOW(53.5)}
  ${PIN_BODY(discColor, glyphMarkup)}
</svg>`;
}

/**
 * Badged 52×80 marker: the pin translated +20 to make room for the satellite badge circle
 * (white ring, disc-coloured inner, glyph), tip at (26, 74) — still 6px above the bottom.
 */
export function createBadgedMarkerSvg(discColor: string, glyphMarkup: string, badgeGlyphMarkup: string): string {
    return `<svg width="${String(MARKER_WIDTH)}" height="${String(BADGED_MARKER_HEIGHT)}" viewBox="0 0 ${String(MARKER_WIDTH)} ${String(BADGED_MARKER_HEIGHT)}" fill="none" xmlns="http://www.w3.org/2000/svg">
  ${GROUND_SHADOW(73.5)}
  <g transform="translate(0 20)">
  ${PIN_BODY(discColor, glyphMarkup)}
  </g>
  <path d="M40 18.5C40 10.4919 33.5081 4 25.5 4C17.4919 4 11 10.4919 11 18.5C11 26.5081 17.4919 33 25.5 33C33.5081 33 40 26.5081 40 18.5Z" fill="white"/>
  <path d="M36 18.5C36 12.701 31.299 8 25.5 8C19.701 8 15 12.701 15 18.5C15 24.299 19.701 29 25.5 29C31.299 29 36 24.299 36 18.5Z" fill="${discColor}"/>
  ${badgeGlyphMarkup}
</svg>`;
}

/**
 * Selected variant: the marker's artwork scaled into the 104×144 canvas with the tip at
 * (52, 84), matching the pre-split selected-pin geometry so the selected layer's centre
 * anchor + [0, -12] offset and the halo layers keep lining up. Interim pending the
 * designer's selected-state pack — derived by scaling, exactly as the old pins were.
 */
export function createSelectedMarkerSvg(discColor: string, glyphMarkup: string): string {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${String(SELECTED_MARKER_WIDTH)}" height="${String(SELECTED_MARKER_HEIGHT)}" viewBox="0 0 ${String(SELECTED_MARKER_WIDTH)} ${String(SELECTED_MARKER_HEIGHT)}" fill="none">
  <g transform="translate(26,30)">
    <path fill-rule="evenodd" clip-rule="evenodd" d="${POI_PIN_PATH}" fill="white"/>
    <path d="${POI_DISC_PATH}" fill="${discColor}"/>
    ${glyphMarkup}
  </g>
</svg>`;
}

// ─── Per-status marker builds ──────────────────────────────────────────────────────────

export interface EntityMarkerImage {
    /** Map image id: poi-marker-<status> / leak-marker-<status> (+ '-selected'). */
    id: string;
    svg: string;
    width: number;
    height: number;
}

export function poiMarkerImageId(status: PoiEntityStatus): string {
    return `poi-marker-${status}`;
}

export function leakMarkerImageId(status: LeakEntityStatus): string {
    return `leak-marker-${status}`;
}

function poiMarkerPair(def: EntityStatusDefinition<PoiEntityStatus>): EntityMarkerImage[] {
    const glyph = POI_GLYPHS[def.status](def.glyphColor);
    return [
        {
            id: poiMarkerImageId(def.status),
            svg: createStandardMarkerSvg(def.discColor, glyph),
            width: MARKER_WIDTH,
            height: STANDARD_MARKER_HEIGHT,
        },
        {
            id: `${poiMarkerImageId(def.status)}-selected`,
            svg: createSelectedMarkerSvg(def.discColor, glyph),
            width: SELECTED_MARKER_WIDTH,
            height: SELECTED_MARKER_HEIGHT,
        },
    ];
}

function leakMarkerPair(def: LeakStatusDefinition): EntityMarkerImage[] {
    const droplet = GLYPH_DROPLET(def.glyphColor);
    const badgeGlyph = LEAK_BADGE_GLYPHS[def.status];
    const svg =
        def.badged && badgeGlyph
            ? createBadgedMarkerSvg(def.discColor, droplet, badgeGlyph(def.glyphColor))
            : createStandardMarkerSvg(def.discColor, droplet);
    return [
        {
            id: leakMarkerImageId(def.status),
            svg,
            width: MARKER_WIDTH,
            height: def.badged && badgeGlyph ? BADGED_MARKER_HEIGHT : STANDARD_MARKER_HEIGHT,
        },
        {
            id: `${leakMarkerImageId(def.status)}-selected`,
            svg: createSelectedMarkerSvg(def.discColor, droplet),
            width: SELECTED_MARKER_WIDTH,
            height: SELECTED_MARKER_HEIGHT,
        },
    ];
}

/**
 * Every marker image the entity map registers: enabled statuses only — disabled statuses
 * render nowhere, which includes never registering an image.
 */
export function buildEntityMarkerImages(): EntityMarkerImage[] {
    return [
        ...POI_ENTITY_STATUSES.filter((d) => d.enabled).flatMap(poiMarkerPair),
        ...LEAK_ENTITY_STATUSES.filter((d) => d.enabled).flatMap(leakMarkerPair),
    ];
}

// ─── Shared-origin indicator (interim, pending UX refinement) ──────────────────────────
//
// Exactly-coincident stacks fan horizontally (centre-to-centre separation fixed at 40px),
// every member's tip sitting at the true coordinate's height. The indicator is one
// bottom-anchored image per stack size: a small dot at the true location with a thin
// hairline spanning the fan, which the fanned tips visually touch.

export const STACK_SEPARATION_PX = 40;
export const FAN_BASE_HEIGHT = 12;

export function fanBaseImageId(stackSize: number): string {
    return `entity-fan-base-${String(stackSize)}`;
}

/** Dot-and-hairline fan base for one stack size. Dot centre = canvas bottom-centre - 6px. */
export function createFanBaseSvg(stackSize: number): string {
    const halfSpan = ((stackSize - 1) / 2) * STACK_SEPARATION_PX;
    const width = String(halfSpan * 2 + 8);
    const centreX = halfSpan + 4;
    const lineY = String(FAN_BASE_HEIGHT - 6);
    return `<svg width="${width}" height="${String(FAN_BASE_HEIGHT)}" viewBox="0 0 ${width} ${String(FAN_BASE_HEIGHT)}" fill="none" xmlns="http://www.w3.org/2000/svg">
  <line x1="${String(centreX - halfSpan)}" y1="${lineY}" x2="${String(centreX + halfSpan)}" y2="${lineY}" stroke="#6B7280" stroke-width="1.5" stroke-linecap="round" stroke-opacity="0.85"/>
  <circle cx="${String(centreX)}" cy="${lineY}" r="3" fill="#6B7280"/>
</svg>`;
}

export interface FanBaseImage {
    id: string;
    svg: string;
    width: number;
    height: number;
    stackSize: number;
}

/** Fan-base images for every stack size the data can produce (pairs up to the measured max of 4). */
export function buildFanBaseImages(maxStackSize = 4): FanBaseImage[] {
    const images: FanBaseImage[] = [];
    for (let size = 2; size <= maxStackSize; size++) {
        const halfSpan = ((size - 1) / 2) * STACK_SEPARATION_PX;
        images.push({
            id: fanBaseImageId(size),
            svg: createFanBaseSvg(size),
            width: halfSpan * 2 + 8,
            height: FAN_BASE_HEIGHT,
            stackSize: size,
        });
    }
    return images;
}
